import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { ChevronDown, HelpCircle, MessageCircle, ArrowRight } from 'lucide-react';
import { mockData } from '../mock';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';

const FAQ = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  
  const filteredQuestions = mockData.faq.questions.filter((item) => {
    const matchesSearch = item.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         item.answer.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || item.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  return (
    <div className="min-h-screen py-16">
      <SEO
        title="Questions Fréquentes"
        description="Retrouvez les réponses à vos questions sur Eau Gratuite : modèle publicitaire, distribution, impact social, tarifs et écologie."
        path="/faq"
      />
      {/* Header Section */}
      <section className="py-16 bg-gradient-to-br from-sky-50 via-white to-emerald-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="flex items-center justify-center w-16 h-16 bg-gradient-to-r from-sky-400 to-emerald-400 rounded-xl mx-auto mb-6">
            <HelpCircle className="h-8 w-8 text-white" />
          </div>
          <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
            {mockData.faq.title}
          </h1>
          <p className="text-xl text-gray-600 leading-relaxed mb-8">
            Trouvez rapidement les réponses à vos questions sur Eau Gratuite
          </p>
          
          {/* Search Bar */}
          <div className="max-w-2xl mx-auto">
            <div className="relative">
              <input
                type="text"
                placeholder="Rechercher une question..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-6 py-4 text-lg border-2 border-sky-200 rounded-xl focus:border-sky-400 focus:outline-none focus:ring-4 focus:ring-sky-100 transition-all duration-200"
              />
              <HelpCircle className="absolute right-4 top-1/2 transform -translate-y-1/2 h-6 w-6 text-gray-400" />
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Category Filter Indicator */}
          {selectedCategory !== 'all' && (
            <div className="mb-8 text-center">
              <div className="inline-flex items-center px-4 py-2 bg-sky-100 rounded-full">
                <span className="text-sky-700 font-medium">
                  Catégorie : {selectedCategory.charAt(0).toUpperCase() + selectedCategory.slice(1)}
                </span>
                <button 
                  onClick={() => setSelectedCategory('all')}
                  className="ml-3 text-sky-600 hover:text-sky-800 font-medium text-sm"
                >
                  × Voir toutes
                </button>
              </div>
            </div>
          )}
          {filteredQuestions.length > 0 ? (
            <Accordion type="single" collapsible className="space-y-4">
              {filteredQuestions.map((item, index) => (
                <AccordionItem 
                  key={index} 
                  value={`item-${index}`}
                  className="border-0 shadow-lg rounded-xl overflow-hidden"
                >
                  <AccordionTrigger className="px-6 py-6 bg-gradient-to-r from-sky-50 to-emerald-50 hover:from-sky-100 hover:to-emerald-100 transition-all duration-200 text-left">
                    <div className="flex items-start space-x-4 w-full">
                      <div className="flex-shrink-0 w-8 h-8 bg-gradient-to-r from-sky-400 to-emerald-400 rounded-full flex items-center justify-center">
                        <span className="text-white font-semibold text-sm">{index + 1}</span>
                      </div>
                      <h3 className="text-lg font-semibold text-gray-900 flex-1">
                        {item.question}
                      </h3>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="px-6 py-6 bg-white">
                    <div className="ml-12">
                      <p className="text-gray-600 leading-relaxed text-lg">
                        {item.answer}
                      </p>
                    </div>
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          ) : (
            <Card className="p-8 text-center border-0 shadow-lg">
              <CardContent className="p-0">
                <HelpCircle className="h-16 w-16 mx-auto text-gray-400 mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  Aucune question trouvée
                </h3>
                <p className="text-gray-600">
                  Essayez avec des mots-clés différents ou contactez-nous directement.
                </p>
              </CardContent>
            </Card>
          )}
        </div>
      </section>

      {/* Categories Section */}
      <section className="py-20 bg-gradient-to-br from-sky-50 to-emerald-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Questions par catégorie
            </h2>
            <p className="text-xl text-gray-600">
              Explorez nos questions les plus fréquentes par thème
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                title: 'Distribution',
                description: 'Comment obtenir votre eau gratuite',
                questions: mockData.faq.questions.filter(q => q.category === 'distribution').length,
                icon: '🚚',
                color: 'sky',
                category: 'distribution'
              },
              {
                title: 'Partenariats',
                description: 'Devenir partenaire publicitaire',
                questions: mockData.faq.questions.filter(q => q.category === 'partenariats').length,
                icon: '🤝',
                color: 'emerald',
                category: 'partenariats'
              },
              {
                title: 'Impact Social',
                description: 'Notre mission avec Ulker\'s',
                questions: mockData.faq.questions.filter(q => q.category === 'impact').length,
                icon: '🌍',
                color: 'sky',
                category: 'impact'
              },
              {
                title: 'Écologie',
                description: 'Notre engagement environnemental',
                questions: mockData.faq.questions.filter(q => q.category === 'ecologie').length,
                icon: '🌱',
                color: 'emerald',
                category: 'ecologie'
              },
              {
                title: 'Compte & Suivi',
                description: 'Gestion de votre profil',
                questions: mockData.faq.questions.filter(q => q.category === 'compte').length,
                icon: '👤',
                color: 'sky',
                category: 'compte'
              },
              {
                title: 'Toutes',
                description: 'Voir toutes les questions',
                questions: mockData.faq.questions.length,
                icon: '📋',
                color: 'emerald',
                category: 'all'
              }
            ].map((category, index) => (
              <Card 
                key={index} 
                className={`p-6 border-0 shadow-lg hover:shadow-xl transition-all duration-300 group cursor-pointer ${
                  selectedCategory === category.category ? 'ring-2 ring-sky-400 bg-sky-50' : 'hover:bg-gray-50'
                }`}
                onClick={() => setSelectedCategory(category.category)}
              >
                <CardContent className="p-0 space-y-4">
                  <div className="text-center">
                    <div className="text-4xl mb-3">{category.icon}</div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">{category.title}</h3>
                    <p className="text-gray-600 text-sm mb-4">{category.description}</p>
                    <div className={`inline-flex items-center px-3 py-1 rounded-full text-sm ${
                      selectedCategory === category.category 
                        ? 'bg-sky-200 text-sky-700' 
                        : `bg-${category.color}-100 text-${category.color}-600`
                    }`}>
                      {category.questions} questions
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Popular Questions */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Questions les plus populaires
            </h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {mockData.faq.questions.slice(0, 4).map((item, index) => (
              <Card key={index} className="p-6 border-0 shadow-lg hover:shadow-xl transition-shadow duration-300">
                <CardContent className="p-0 space-y-4">
                  <div className="flex items-start space-x-3">
                    <div className="flex-shrink-0 w-8 h-8 bg-gradient-to-r from-sky-400 to-emerald-400 rounded-full flex items-center justify-center">
                      <span className="text-white font-semibold text-sm">{index + 1}</span>
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-gray-900 mb-3">
                        {item.question}
                      </h3>
                      <p className="text-gray-600 leading-relaxed">
                        {item.answer}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Help Section */}
      <section className="py-20 bg-gradient-to-br from-emerald-50 to-sky-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Card className="p-8 border-0 shadow-xl bg-white">
            <CardContent className="p-0 text-center space-y-6">
              <div className="flex items-center justify-center w-16 h-16 bg-gradient-to-r from-emerald-400 to-sky-400 rounded-xl mx-auto">
                <MessageCircle className="h-8 w-8 text-white" />
              </div>
              <h2 className="text-3xl font-bold text-gray-900">
                Vous ne trouvez pas votre réponse ?
              </h2>
              <p className="text-xl text-gray-600 leading-relaxed">
                Notre équipe est là pour vous aider. Contactez-nous et nous vous répondrons rapidement.
              </p>
              <div className="flex justify-center">
                <Link to="/contact">
                  <Button 
                    size="lg"
                    className="bg-gradient-to-r from-emerald-500 to-sky-500 hover:from-emerald-600 hover:to-sky-600 text-white px-8 py-4 rounded-xl font-semibold transition-all duration-200 shadow-lg hover:shadow-xl group"
                  >
                    Nous contacter
                    <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform duration-200" />
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Quick Links */}
      <section className="py-16 bg-white border-t border-gray-100">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              Liens utiles
            </h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            {[
              { title: 'Comment ça marche', link: '/comment-ca-marche', description: 'Comprendre notre modèle' },
              { title: 'Notre mission', link: '/mission', description: 'Découvrir notre impact' },
              { title: 'Devenir partenaire', link: '/partenaires', description: 'Rejoindre le mouvement' },
              { title: 'Nous contacter', link: '/contact', description: 'Obtenir de l\'aide' }
            ].map((item, index) => (
              <Link key={index} to={item.link}>
                <Card className="p-4 border border-gray-200 hover:border-sky-300 hover:shadow-md transition-all duration-200 group">
                  <CardContent className="p-0 text-center">
                    <h3 className="font-semibold text-gray-900 mb-1 group-hover:text-sky-600 transition-colors duration-200">
                      {item.title}
                    </h3>
                    <p className="text-sm text-gray-600">{item.description}</p>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default FAQ;